'use client';

import { IProductDetail, IVipPrice } from '@/api/types/public';
import lensInfoClassName from '@/app/_lens-components/style/lens-info';
import { processClassName } from '@/app/_lens-components/style/responsive';
import AccessiblePrice from '@/components/AccessiblePrice';
import Dialog from '@/components/Dialog';
import Image from '@/components/Image';
import Popup from '@/components/Popup';
import MemberTag from '@/components/Product/MemberTag';
import { useReporting } from '@/hooks/useReporting';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { getRouteCnName } from '@/utils/route-metadata';
import { CollapseArrowUpOutlined, NewCloseOutlined } from '@zeelool/icon';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

const ClientOnlyPrice = dynamic(() => import('@/components/Product/Price'), { ssr: false });
const PURCHASE_METHOD_POPUP_TYPE = '选择加购方式弹窗';

interface PurchaseMethodDialogProps {
  open: boolean;
  product: IProductDetail;
  isWeb: boolean;
  onClose: () => void;
  onSelectLenses: () => void;
  onAddLater: () => void;
  onFrameOnly: () => void;
  addLaterLoading: boolean;
  frameOnlyLoading: boolean;
  selectLensesLoading: boolean;
}

interface PurchaseMethodOptionProps {
  title?: string;
  description: string;
  isWeb: boolean;
  prefix?: string;
  onClick?: () => void;
  disabled?: boolean;
}

function PurchaseMethodOption({
  title,
  description,
  isWeb,
  prefix,
  onClick,
  disabled = false,
}: PurchaseMethodOptionProps) {
  const className = `rounded-[4px] border border-[#e0e0e0] bg-white ${isWeb ? 'px-[16px] py-[20px] xl:border-0' : 'p-[16px]'}`;
  const content = (
    <>
      <div
        className={`flex items-center text-[#282828] ${isWeb ? 'gap-[12px] text-[18px] leading-[24px]' : 'gap-[8px] text-[14px] leading-[16px]'}`}
      >
        {prefix && <strong className="font-bs">{prefix}</strong>}
        <span className="font-br">{title}</span>
      </div>
      <p
        className={`mt-[10px] font-br text-[#6c6c6c] ${isWeb ? 'text-[14px] leading-[16px]' : 'text-[12px] leading-[14px]'}`}
      >
        {description}
      </p>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={`${className} w-full text-left ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        disabled={disabled}
        aria-busy={disabled}
        onClick={onClick}
      >
        {content}
      </button>
    );
  }

  return <section className={className}>{content}</section>;
}

export default function PurchaseMethodDialog({
  open,
  product,
  isWeb,
  onClose,
  onSelectLenses,
  onAddLater,
  onFrameOnly,
  addLaterLoading,
  frameOnlyLoading,
  selectLensesLoading,
}: PurchaseMethodDialogProps) {
  const t = useTranslations('goodsDetail.purchaseMethod');
  const tPriceA11y = useTranslations('goodsDetail.clientPrice');
  const userInfo = useStore((state) => state.userInfo);
  const pathname = usePathname();
  const { trackEvent } = useReporting();
  const hasTrackedExposureRef = useRef(false);
  const optionClickLockedRef = useRef(false);
  const productImage = (product.image?.product?.[0] as { url?: string } | undefined)?.url || '';
  const priceObject = product as unknown as IVipPrice;
  const [showFrameDetailsPopup, setShowFrameDetailsPopup] = useState(false);
  const [popupContainer, setPopupContainer] = useState<HTMLElement | null>(null);
  const canSelectLenses =
    product.is_glass === 1 && product.product_lens === 1 && product.is_show_add_lense === 1;
  const canChooseFrameOnly =
    product.is_glass === 1 && product.product_lens === 1 && product.is_frame_only === 1;
  const promotionType = String(product.products_promotions_type);
  const showMemberTag =
    userInfo?.is_member === 1 &&
    product[`discount_percent_v${userInfo.member_level}`] > 0 &&
    promotionType !== '3' &&
    promotionType !== '4';
  const frameDetailsButtonLabel = showFrameDetailsPopup
    ? t('hideFrameDetails')
    : t('showFrameDetails');

  const trackPopupClick = useCallback(
    (buttonName: string) => {
      trackEvent(
        {
          eventName: 'PopUp_Click',
          properties: {
            page_name: getRouteCnName(pathname),
            url_path: window.location.href,
            tab_name: '',
            popup_type: PURCHASE_METHOD_POPUP_TYPE,
            btn_name: buttonName,
          },
        },
        [ProviderType.SENSORS],
      );
    },
    [pathname, trackEvent],
  );

  useEffect(() => {
    if (!open) {
      hasTrackedExposureRef.current = false;
      return;
    }
    if (hasTrackedExposureRef.current) return;

    hasTrackedExposureRef.current = true;
    trackEvent(
      {
        eventName: 'PopUp_Exp',
        properties: {
          page_name: getRouteCnName(pathname),
          url_path: window.location.href,
          popup_type: PURCHASE_METHOD_POPUP_TYPE,
        },
      },
      [ProviderType.SENSORS],
    );
  }, [open, pathname, trackEvent]);

  const handleOptionClick = (buttonName: string, onClick: () => void) => {
    if (optionClickLockedRef.current) return;

    optionClickLockedRef.current = true;
    window.setTimeout(() => {
      optionClickLockedRef.current = false;
    }, 300);

    trackPopupClick(buttonName);
    onClick();
  };
  const optionLoading = addLaterLoading || frameOnlyLoading || selectLensesLoading;

  return (
    <Dialog
      open={open}
      ariaLabel={t('title')}
      aria-label={t('title')}
      hideHeader
      footer={null}
      onClose={onClose}
      className="!z-[450]"
      contentProps={{ className: '!overflow-hidden !p-0' }}
      sx={{
        '& .MuiDialogTitle-root': {
          padding: 0,
        },
        '& .MuiDialog-container': {
          alignItems: 'stretch',
          height: '100%',
        },
        '& .MuiPaper-root': {
          margin: 0,
          width: '100vw',
          maxWidth: '100vw',
          height: '100%',
          maxHeight: '100%',
          borderRadius: 0,
        },
        '& .MuiDialogContent-root': {
          padding: 0,
          height: '100%',
          maxHeight: '100%',
          overflow: 'hidden',
        },
      }}
    >
      <div className="box-border min-h-full bg-[#f4f4f4] max-xl:pt-[64px] max-xl:pb-[100px] xl:flex xl:h-full xl:bg-[#f8f8f8] xl:pb-[87px]">
        <section
          className={`hidden bg-white xl:flex xl:h-full xl:overflow-y-auto xl:pb-0 ${processClassName.leftPart}`}
        >
          <div className={`${processClassName.leftPartSide} flex-1`} />
          <div className={`relative flex-col ${processClassName.leftPartBox}`}>
            <div className="relative w-full h-auto px-[27px] max-w-[534px] mt-[16vh] mb-[32px] aspect-[2/1] !mix-blend-multiply">
              <Image
                src={productImage}
                alt={product.short_name || ''}
                width={800}
                height={400}
                className="absolute top-0 left-0 block w-full h-auto z-6"
              />
            </div>
            <div className={`${processClassName.leftBottom}`}>
              <p className="text-[14px] font-bs mb-[12px] mt-[24px]">{t('frame')}</p>
              <div className="flex justify-between items-center mt-[8px] text-[14px]">
                <p>{product.short_name || ''}</p>
                <div className="flex items-center">
                  {showMemberTag && (
                    <MemberTag
                      level={userInfo.member_level}
                      className={lensInfoClassName.memberTag}
                    />
                  )}
                  <ClientOnlyPrice
                    priceObject={priceObject}
                    renderPrice={(price, sellPrice) => (
                      <div className="flex items-center gap-[5px]">
                        {price !== sellPrice && (
                          <AccessiblePrice
                            as="del"
                            value={price}
                            label={tPriceA11y('srOriginal')}
                            className={lensInfoClassName.originalPrice(false)}
                          />
                        )}
                        <AccessiblePrice
                          value={sellPrice}
                          label={tPriceA11y('srCurrent')}
                          className="font-bb text-[16px]"
                        />
                      </div>
                    )}
                  />
                </div>
              </div>

              {product.product_frame_color_name && (
                <div className="mt-[8px] text-[14px]">—{product.product_frame_color_name}</div>
              )}
            </div>
          </div>
        </section>

        <section className={`${processClassName.rightPart} h-full overflow-y-auto bg-[#f4f4f4]`}>
          <div className={`${processClassName.rightPartContent} h-full`}>
            <div
              id="purchase-method-right-box-content"
              ref={setPopupContainer}
              className={`relative ${processClassName.rightPartBox} max-xl:px-[14px] xl:pt-[79px]`}
            >
              <div className="max-xl:fixed max-xl:right-[14px] max-xl:top-[12px] z-[110] text-[#333333] text-[24px] xl:absolute xl:right-0 xl:top-[24px] xl:z-10 xl:text-[24px]">
                <button
                  type="button"
                  className="flex cursor-pointer items-center justify-center border-0 bg-transparent p-0"
                  aria-label={t('close')}
                  onClick={onClose}
                >
                  <NewCloseOutlined className="cursor-pointer" aria-hidden />
                </button>
              </div>
              <h2
                className={`font-bb leading-[normal] text-[#282828] ${isWeb ? 'text-[22px]' : 'text-[20px]'}`}
              >
                {t('title')}
              </h2>
              <div className="mt-[26px] flex flex-col gap-[12px] xl:mt-[30px]">
                {canSelectLenses && (
                  <>
                    <PurchaseMethodOption
                      prefix={t('yes')}
                      title={t('addNow')}
                      description={t('addNowDescription')}
                      isWeb={isWeb}
                      onClick={() => handleOptionClick('Yes Add it now', onSelectLenses)}
                      disabled={optionLoading}
                    />
                    <PurchaseMethodOption
                      prefix={t('no')}
                      title={t('addLater')}
                      description={t('addLaterDescription')}
                      isWeb={isWeb}
                      onClick={() => handleOptionClick('No Add it later', onAddLater)}
                      disabled={optionLoading}
                    />
                  </>
                )}
                {canChooseFrameOnly && (
                  <PurchaseMethodOption
                    prefix={t('noPrescription')}
                    description={t('noPrescriptionDescription')}
                    isWeb={isWeb}
                    onClick={() => handleOptionClick("I don't need one", onFrameOnly)}
                    disabled={optionLoading}
                  />
                )}
              </div>
            </div>
            <div className={processClassName.rightPartSide} />
          </div>
        </section>
      </div>

      <footer className="fixed bottom-0 left-0 right-0 z-[100] flex h-[69px] items-center bg-white max-xl:px-[14px] shadow-[0px_-2px_10px_rgba(204,204,204,0.5)] xl:h-[87px] xl:px-0 rounded-t-[12px]">
        <button
          type="button"
          aria-label={frameDetailsButtonLabel}
          aria-expanded={showFrameDetailsPopup}
          aria-controls={showFrameDetailsPopup ? 'purchase-method-frame-details' : undefined}
          className="flex w-full items-center border-0 bg-transparent p-0 text-left xl:hidden"
          onClick={() => setShowFrameDetailsPopup((visible) => !visible)}
        >
          <Image
            src={productImage}
            alt={product.short_name || ''}
            width={60}
            height={30}
            className="h-[30px] w-[60px] object-contain mix-blend-multiply"
          />
          <div className="ml-[12px] min-w-0">
            <p className="truncate font-br text-[12px] leading-[16px] text-[#282828]">
              {product.short_name || ''}
            </p>
            <ClientOnlyPrice
              priceObject={priceObject}
              renderPrice={(_price, sellPrice) => (
                <AccessiblePrice
                  value={sellPrice}
                  label={tPriceA11y('srCurrent')}
                  className="flex justify-start items-start flex-col mt-[4px]"
                  visualClassName="font-bb f14"
                />
              )}
            />
          </div>
          <CollapseArrowUpOutlined
            className={`ml-[12px] text-[18px] text-[#89909a] ${showFrameDetailsPopup ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
        <div className={`${processClassName.leftPart}`}>
          <div className={processClassName.leftPartSide} />
          <div className={`flex justify-between ${processClassName.leftPartBox}`}>
            <p className="font-bs text-[16px] text-[#282828]">{t('subtotal')}</p>
            <ClientOnlyPrice
              priceObject={priceObject}
              renderPrice={(price, sellPrice) => (
                <div className="flex items-center gap-[8px]">
                  {price !== sellPrice && (
                    <AccessiblePrice
                      as="del"
                      value={price}
                      label={tPriceA11y('srOriginal')}
                      className="font-br text-[16px] text-[#6c6c6c] line-through"
                    />
                  )}
                  <AccessiblePrice
                    value={sellPrice}
                    label={tPriceA11y('srCurrent')}
                    className="font-bb text-[28px]"
                  />
                </div>
              )}
            />
          </div>
        </div>
        <div className={`${processClassName.rightPart} hidden xl:block`}>
          <div className={processClassName.rightPartContent}>
            <div className={processClassName.rightPartBox} />
            <div className={processClassName.rightPartSide} />
          </div>
        </div>
      </footer>

      {popupContainer && (
        <Popup
          open={showFrameDetailsPopup}
          className="price-desc-diag-area"
          bodyClassName="!pt-[0]"
          container={popupContainer}
          title="&nbsp;"
          ariaLabel={t('frameDetails')}
          contentContainerId="purchase-method-frame-details"
          showDivider={false}
          zIndex={90}
          round
          position="bottom"
          onClose={() => setShowFrameDetailsPopup(false)}
        >
          <div className={lensInfoClassName.container}>
            <div className={lensInfoClassName.sectionTitle}>{t('frame')}</div>
            <div className={lensInfoClassName.contentRow}>
              <div>{product.short_name || ''}</div>
              <div className={lensInfoClassName.priceContainer}>
                {showMemberTag && (
                  <MemberTag
                    level={userInfo.member_level}
                    className={lensInfoClassName.memberTag}
                  />
                )}
                <ClientOnlyPrice
                  priceObject={priceObject}
                  renderPrice={(price, sellPrice) => (
                    <>
                      {price !== sellPrice && (
                        <AccessiblePrice
                          as="del"
                          value={price}
                          label={tPriceA11y('srOriginal')}
                          className={lensInfoClassName.originalPrice(false)}
                        />
                      )}
                      <AccessiblePrice
                        value={sellPrice}
                        label={tPriceA11y('srCurrent')}
                        className={lensInfoClassName.currentPrice}
                      />
                    </>
                  )}
                />
              </div>
            </div>
            {product.product_frame_color_name && (
              <div className={lensInfoClassName.colorDescription}>
                —{product.product_frame_color_name}
              </div>
            )}
          </div>
        </Popup>
      )}
    </Dialog>
  );
}
